// A comment to trigger a reload of the language server.
import React, { useCallback, useState } from 'react'
import { Typography } from '~/components/common/Typography'
import { CheckIcon, CopyIcon, EyeIcon, EyeSlashIcon } from '~/components/icons'
import { cm } from '~/utils/utils'

const useCopyToClipboard = (): [(text: string) => void, boolean] => {
  const [isCopied, setIsCopied] = useState(false)

  const copy = useCallback((text: string) => {
    if (!text) return
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true)
      setTimeout(() => {
        setIsCopied(false)
      }, 2000)
    })
  }, [])

  return [copy, isCopied]
}

interface CopySnippetProps {
  text: string
  isSecret?: boolean
  code?: string
  className?: string
}

export const CopySnippet: React.FC<CopySnippetProps> = ({
  text,
  isSecret = false,
  code = '',
  className = ''
}) => {
  const [copy, isCopied] = useCopyToClipboard()
  const [isHidden, setIsHidden] = useState(isSecret)

  const onCopy = () => {
    copy(code)
  }

  const toggleHidden = () => {
    setIsHidden(!isHidden)
  }

  // Shared: Copy button and text
  const copyButton = (
    <button
      onClick={onCopy}
      className='flex w-9 h-9 items-center justify-center'
      aria-label='Copy button'
    >
      {isCopied ? <CheckIcon className='size-4' /> : <CopyIcon className='size-4' />}
    </button>
  )

  let displayedText: string
  let textClass: string

  if (isSecret) {
    displayedText = isHidden ? (isCopied ? 'Token Copied' : text) : code
    textClass = `break-all whitespace-pre-wrap ${isHidden ? 'text-tokens-neutral-text-primary' : 'text-tokens-neutral-text-tertiary'}`
  } else {
    displayedText = code
    textClass = 'break-all whitespace-pre-wrap text-tokens-neutral-text-tertiary'
  }

  return (
    <div
      className={cm(
        `bg-tokens-neutral-bg-grey-2 rounded-xl flex py-4 px-3 items-center justify-center`,
        className
      )}
    >
      {copyButton}
      <div className='flex-1 flex items-center ml-[10px]'>
        <Typography fontFamily='geistMono' fontSize='labelSm' className={textClass}>
          {displayedText}
        </Typography>
      </div>
      {isSecret && (
        <button onClick={toggleHidden} className='flex w-9 h-9 items-center justify-center'>
          {isHidden ? <EyeSlashIcon className='size-4' /> : <EyeIcon className='size-4' />}
        </button>
      )}
    </div>
  )
}
